import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import type { YesNoUnknown } from "../OnboardingAPI";
import { CHOICES } from "./useOnboardingController";

export const selectClass =
  "flex h-10 w-full rounded-md border border-input bg-transparent px-3 text-sm shadow-xs outline-none transition-[color,box-shadow] focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-60 max-sm:h-11";

export function Field({
  id,
  label,
  hint,
  required,
  error,
  className,
  children,
}: {
  id: string;
  label: string;
  hint?: string;
  required?: boolean;
  error?: string | null;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={cn("space-y-1.5", className)}>
      <Label htmlFor={id} className="text-sm font-medium">
        {label}
        {required && (
          <span aria-hidden="true" className="ml-0.5 text-destructive">
            *
          </span>
        )}
      </Label>
      {children}
      {hint && !error && (
        <p id={`${id}-hint`} className="text-xs text-muted-foreground">
          {hint}
        </p>
      )}
      {error && (
        <p id={`${id}-error`} className="text-xs text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}

export function ChoiceGroup({
  name,
  legend,
  hint,
  value,
  disabled,
  onChange,
}: {
  name: string;
  legend: string;
  hint?: string;
  value: YesNoUnknown | undefined;
  disabled: boolean;
  onChange: (value: YesNoUnknown) => void;
}) {
  const { t } = useTranslation();
  return (
    <fieldset className="space-y-2">
      <legend className="text-sm font-medium">{legend}</legend>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
      <div className="flex flex-wrap gap-2">
        {CHOICES.map((choice) => (
          <label key={choice} className={cn(disabled ? "cursor-default" : "cursor-pointer")}>
            <input
              type="radio"
              name={name}
              value={choice}
              checked={value === choice}
              disabled={disabled}
              onChange={() => onChange(choice)}
              className="peer sr-only"
            />
            <span className="inline-flex h-10 items-center rounded-full border border-input px-4 text-sm transition-colors peer-checked:border-primary peer-checked:bg-primary peer-checked:text-primary-foreground peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-focus-visible:ring-offset-2 peer-disabled:opacity-70 max-sm:h-11">
              {t(`onboardingPage.choice.${choice}`)}
            </span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

